import { EstimateValue } from "@/discovery/edit-mode";
import { LeadModeBadge } from "@/components/LeadModeBadge";
import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useEffect } from "react";
import {
  ArrowUpRight,
  Tags,
  UserPlus,
  RefreshCw,
  Rocket,
  Upload,
  TrendingUp,
  Timer,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { PageHeader } from "@/components/PageHeader";
import { IntelligencePanel } from "@/components/IntelligencePanel";
import { cn } from "@/lib/utils";
import { pageTitle } from "@/lib/brand";
import { useDashboardStore } from "@/stores/dashboardStore";
import { useLeadsStore } from "@/stores/leadsStore";
import { useCustomerStore } from "@/stores/customerStore";
import { EmptyState, ErrorState, LoadingState } from "@/components/DataState";
import { useDashboardAccess } from "@/components/dashboard-access-context";
import { canViewOwnerOperations } from "@/lib/access-control";
import { useInitialSplashReady } from "@/hooks/useInitialSplashReady";

const DashboardConversionChart = lazy(() => import("@/components/DashboardConversionChart"));

export const Route = createFileRoute("/app/$customerId/")({
  head: () => ({
    meta: [
      { title: pageTitle("Overview") },
      {
        name: "description",
        content:
          "Discoveries, completed results, qualified leads and pipeline value across your live Scenario.",
      },
      {
        property: "og:title",
        content: pageTitle("Overview"),
      },
      {
        property: "og:description",
        content:
          "Live view of Discoveries, estimates and qualified leads.",
      },
    ],
  }),
  component: Dashboard,
});

const activityIcons = {
  pricing: Tags,
  lead: UserPlus,
  sync: RefreshCw,
  publish: Rocket,
  upload: Upload,
} as const;

function Dashboard() {
  const { customerId } = Route.useParams();
  const access = useDashboardAccess();
  const canViewOperations = canViewOwnerOperations(customerId, access);
  const { t, tl, formatCurrency, formatNumber } = useI18n();
  const { analytics, discoveries, activity, status, error, currency: analyticsCurrency } = useDashboardStore();
  const leads = useLeadsStore((state) => state.leads);
  const activeCurrency = useCustomerStore((state) => state.settings?.profile.currency ?? "USD");
  // Keep displayed monetary units paired with the aggregate's subscription snapshot.
  const currency = analyticsCurrency ?? activeCurrency;
  const leadsStatus = useLeadsStore((state) => state.status);
  useInitialSplashReady(!canViewOperations || (
    status !== "idle" && status !== "loading" && leadsStatus !== "idle" && leadsStatus !== "loading"
  ));

  useEffect(() => {
    if (!canViewOperations) {
      useDashboardStore.getState().stop();
      useLeadsStore.getState().stop();
      return;
    }
    useDashboardStore.getState().start(customerId, activeCurrency);
    useLeadsStore.getState().start(customerId);
    return () => {
      useDashboardStore.getState().stop();
      useLeadsStore.getState().stop();
    };
  }, [customerId, canViewOperations, activeCurrency]);

  if (!canViewOperations) return <div><PageHeader title={t("dash.title")} subtitle={t("dash.subtitle")} /><EmptyState message="Operational activity is available only to authorized company users." /></div>;

  const totals = analytics.reduce(
    (sum, day) => ({
      discoveries: sum.discoveries + day.discoveries,
      results: sum.results + day.results,
      leads: sum.leads + day.leads,
      value: sum.value + day.pipelineValue,
      estimateTotal: sum.estimateTotal + day.estimateTotal,
      estimateCount: sum.estimateCount + day.estimateCount,
      duration: sum.duration + day.estimateDurationSecondsTotal,
      durationCount: sum.durationCount + day.estimateDurationCount,
      started: sum.started + day.started,
      quotes: sum.quotes + day.quoteRequests,
    }),
    { discoveries: 0, results: 0, leads: 0, value: 0, estimateTotal: 0, estimateCount: 0, duration: 0, durationCount: 0, started: 0, quotes: 0 },
  );
  const lastSeven = analytics.slice(-7);
  const previousSeven = analytics.slice(-14, -7);
  const delta = (key: "discoveries" | "results" | "leads" | "pipelineValue") => {
    const current = lastSeven.reduce((sum, day) => sum + day[key], 0);
    const previous = previousSeven.reduce((sum, day) => sum + day[key], 0);
    return previous ? Math.round(((current - previous) / previous) * 1000) / 10 : 0;
  };
  const kpis = [
    { key: "dash.kpi.discoveries", value: totals.discoveries, delta: delta("discoveries"), spark: lastSeven.map((day) => day.discoveries) },
    { key: "dash.kpi.results", value: totals.results, delta: delta("results"), spark: lastSeven.map((day) => day.results) },
    { key: "dash.kpi.leads", value: totals.leads, delta: delta("leads"), spark: lastSeven.map((day) => day.leads) },
    { key: "dash.kpi.value", value: totals.value, delta: delta("pipelineValue"), currency: true, spark: lastSeven.map((day) => day.pipelineValue) },
  ];
  const conversionSeries = lastSeven.map((day) => ({ label: day.date.toLocaleDateString("en-US", { weekday: "short" }), discoveries: day.discoveries, results: day.results, leads: day.leads }));
  const denominator = Math.max(1, totals.discoveries);
  const funnel = [
    { label: { en: "Discovery created", he: "נוצר תהליך גילוי" }, value: 100 },
    { label: { en: "Started discovery", he: "התחילו גילוי" }, value: Math.round((totals.started / denominator) * 100) },
    { label: { en: "Reached a result", he: "הגיעו לתוצאה" }, value: Math.round((totals.results / denominator) * 100) },
    { label: { en: "Requested quote", he: "ביקשו הצעה" }, value: Math.round((totals.quotes / denominator) * 100) },
  ];

  if (status === "loading" || status === "idle" || leadsStatus === "loading" || leadsStatus === "idle") return <div><PageHeader title={t("dash.title")} subtitle={t("dash.subtitle")} /><LoadingState label="Loading dashboard…" /></div>;
  if (status === "error") return <div><PageHeader title={t("dash.title")} subtitle={t("dash.subtitle")} /><ErrorState error={error} /></div>;
  if (status === "empty") return <div><PageHeader title={t("dash.title")} subtitle={t("dash.subtitle")} /><EmptyState message="No dashboard activity yet." /></div>;

  return (
    <div>
      <PageHeader title={t("dash.title")} subtitle={t("dash.subtitle")} />

      <IntelligencePanel
        eyebrow={t("dash.insight.eyebrow")}
        title={t("dash.insight.title")}
        body={t("dash.insight.body")}
        value={88}
        valueLabel={t("dash.insight.quality")}
        action={
          <Link
            to="/app/$customerId/pricing"
            params={{ customerId }}
            className="app-chip border-discovery/20 px-3 py-2 text-xs font-bold text-discovery-foreground transition-colors hover:bg-discovery-soft"
          >
            {t("dash.insight.action")}
            <ArrowUpRight className="size-3.5 rtl:-scale-x-100" aria-hidden />
          </Link>
        }
      />

      <p className="mt-5 text-xs text-muted-foreground">
        {t("dash.currencyTotals").replace("{currency}", currency)}
      </p>
      <section className="mt-3 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((kpi, i) => (
          <article
            key={kpi.key}
            className={cn(
              "lift animate-fade-up p-5",
              i === 1
                ? "surface-elevated border-discovery/20"
                : i === 2
                  ? "surface border-conversion/20"
                  : "surface",
            )}
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <p className="text-xs font-semibold text-muted-foreground">
              {t(kpi.key)}
            </p>
            <p className="num mt-2 text-2xl font-extrabold">
              {kpi.currency
                ? formatCurrency(kpi.value, currency)
                : formatNumber(kpi.value)}
            </p>
            <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-success">
              <TrendingUp className="size-3.5" aria-hidden />
              {kpi.delta}%
              <span className="font-medium text-muted-foreground">
                {t("dash.conversionSub")}
              </span>
            </div>
            <div className="mt-4 flex h-8 items-end gap-1">
              {kpi.spark.map((v, idx) => (
                <span
                  key={idx}
                  className={cn(
                    "flex-1 rounded-sm",
                    i === 1
                      ? "bg-discovery-soft"
                      : i === 2
                        ? "bg-conversion-soft"
                        : "bg-primary-soft",
                  )}
                  style={{ height: `${(v / Math.max(...kpi.spark)) * 100}%` }}
                />
              ))}
            </div>
          </article>
        ))}
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[1.6fr_1fr]">
        <article className="surface-elevated animate-fade-up p-5">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-base font-bold">{t("dash.conversion")}</h2>
            <span className="text-xs text-muted-foreground">
              {t("dash.conversionSub")}
            </span>
          </div>
          <div className="mt-5 h-56">
            <Suspense fallback={<div className="h-full animate-pulse rounded-2xl bg-secondary/60" aria-label="Loading conversion chart" />}>
              <DashboardConversionChart data={conversionSeries} />
            </Suspense>
          </div>
          <div className="mt-4 grid gap-3 border-t border-border pt-4 sm:grid-cols-2">
            <div className="flex items-center gap-2 text-sm">
              <TrendingUp className="size-4 text-primary" aria-hidden />
              <span className="text-muted-foreground">
                {t("dash.avgEstimate")}
              </span>
              <span className="num ms-auto font-bold">
                {totals.estimateCount ? formatCurrency(totals.estimateTotal / totals.estimateCount, currency) : "—"}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Timer className="size-4 text-teal" aria-hidden />
              <span className="text-muted-foreground">{t("dash.avgTime")}</span>
              <span className="num ms-auto font-bold">0:{String(totals.durationCount ? Math.round(totals.duration / totals.durationCount) : 0).padStart(2, "0")}</span>
            </div>
          </div>
        </article>

        <article className="surface animate-fade-up p-6">
          <h2 className="text-base font-bold">{t("dash.funnel")}</h2>
          <ul className="mt-5 space-y-4">
            {funnel.map((step) => (
              <li key={step.value}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">{tl(step.label)}</span>
                  <span className="num text-muted-foreground">
                    {step.value}%
                  </span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
                  <div
                    className="discovery-progress h-full rounded-full transition-[width] duration-700"
                    style={{ width: `${step.value}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </article>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-2">
        <article className="surface animate-fade-up overflow-hidden">
          <div className="flex items-center justify-between gap-3 p-5 pb-3">
            <h2 className="text-base font-bold">{t("dash.recentLeads")}</h2>
            <Link
              to="/app/$customerId/leads"
              params={{ customerId }}
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary"
            >
              {t("dash.viewAll")}
              <ArrowUpRight className="size-3.5 rtl:-scale-x-100" aria-hidden />
            </Link>
          </div>
          <ul className="divide-y divide-border">
            {leads.slice(0, 4).map((lead) => (
              <li key={lead.id}>
                <Link
                  to="/app/$customerId/leads"
                  params={{ customerId }}
                  className="app-row flex items-center gap-3 px-5 py-3.5"
                >
                  <span className="grid size-9 shrink-0 place-items-center rounded-full bg-conversion-soft text-xs font-bold text-conversion-foreground">
                    {lead.name.slice(0, 2)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 text-sm font-semibold">
                      <span className="truncate">{lead.name}</span>
                      <LeadModeBadge mode={lead.mode} />
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {tl(lead.solution)}
                    </span>
                  </span>
                  <span className="num shrink-0 text-sm font-bold">
                    {lead.estimatePresentation?.partial && <span className="block text-xs">{t("disc.partialEstimate")}</span>}{lead.estimatePresentation ? <EstimateValue estimate={lead.estimatePresentation.estimate} display={lead.estimatePresentation.display} /> : lead.estimateLow === null ? t("disc.quoteRequired") : formatCurrency(lead.estimateLow, lead.estimateCurrency)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </article>

        <article className="surface animate-fade-up overflow-hidden">
          <div className="p-5 pb-3">
            <h2 className="text-base font-bold">
              {t("dash.recentConversations")}
            </h2>
          </div>
          <ul className="divide-y divide-border">
            {discoveries.map((s) => (
              <li key={s.id} className="app-row flex items-center gap-3 px-5 py-3.5">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">
                    {tl(s.label)}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {tl(s.outcome)} · {formatRelativeTime(s.updatedAt, tl)}
                  </span>
                </span>
                <span className="shrink-0 text-end">
                  <span className="num block text-sm font-bold">
                    {s.estimatePresentation?.partial && <span className="block text-xs">{t("disc.partialEstimate")}</span>}{s.estimatePresentation ? <EstimateValue estimate={s.estimatePresentation.estimate} display={s.estimatePresentation.display} /> : s.estimate === null ? t("disc.quoteRequired") : formatCurrency(s.estimate, s.estimateCurrency ?? currency)}
                  </span>
                  {s.confidence !== null && <span className="mt-0.5 inline-flex items-center justify-end gap-1 text-[11px] text-muted-foreground">
                    <span
                      className="size-1.5 rounded-full bg-discovery"
                      aria-hidden
                    />
                    {t("leads.confidence")} {s.confidence}%
                  </span>}
                </span>
              </li>
            ))}
          </ul>
        </article>
      </section>

      <section className="surface animate-fade-up mt-5 p-6">
        <h2 className="text-base font-bold">{t("dash.activity")}</h2>
        <ul className="mt-4 space-y-1">
          {activity.map((item, i) => {
            const Icon = activityIcons[item.kind];
            return (
              <li
                key={i}
                className="app-row flex items-start gap-3 rounded-xl p-2.5"
              >
                <span
                  className={cn(
                    "mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg",
                    item.kind === "lead"
                      ? "bg-conversion-soft text-conversion-foreground"
                      : item.kind === "pricing"
                        ? "bg-teal-soft text-teal"
                        : "bg-primary-soft text-primary",
                  )}
                >
                  <Icon className="size-4" aria-hidden />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">
                    {tl(item.text)}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {formatRelativeTime(item.createdAt, tl)}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

function formatRelativeTime(date: Date, tl: (value: { en: string; he: string }) => string) {
  const minutes = Math.max(1, Math.round((Date.now() - date.getTime()) / 60_000));
  if (minutes < 60) return tl({ en: `${minutes} min ago`, he: `לפני ${minutes} דקות` });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return tl({ en: `${hours}h ago`, he: `לפני ${hours} שעות` });
  const days = Math.round(hours / 24);
  return tl({ en: `${days}d ago`, he: `לפני ${days} ימים` });
}
